import type { BoxRef } from '../../../../../../mads';
import type { IndeedApplyButtonContainer } from '../../../../viewjobOneGraphModel';

import { Button, type ButtonSize, type ButtonVariant } from '../../../../../../mads/Button';
import { useI18n } from '../../../../../../shared/I18n';
import { useLogErrorOnce } from '../../../../../../shared/logging/useLogErrorOnce';
import { useTrackVisibility } from '../../../../../../shared/logging/visibility-tracker';
import { logTapButton } from '../../../../../../shared/native-modules/tare/logTapButton';
import { useTareData } from '../../../../../../shared/native-modules/tare/TareDataContext';
import eventEmitter, { ApplyType, ApplyStatus, NativeApplyResult } from '../../../../../../shared/util/eventEmitter';
import { useHandleStartIndeedApply } from '../../../../../native-module/handleStartIndeedApply';
import { updateApplyStartCookie } from '../../../../../native-module/updateApplyStartCookie';
import { getEnvironmentConfig } from '../../../../config/urls';
import { useDwellTiming } from '../../../../context/DwellTiming';
import { useViewJobDataContext, useViewjobOneGraphModel } from '../../../../context/ViewJobDataContext';
import { useEmployerOutreachInfo } from '../../../../hooks/useEmployerOutreachInfo';
import { useEmployerOutreachSource } from '../../../../hooks/useEmployerOutreachSource';
import { handleEmployerOutreachApply } from '../EmployerOutreach/utils/handleEmployerOutreachApply';
import { logApplyButtonShowEvent, type ButtonDimensions } from '../utils/applyButtonLogging';

import { RNApplyButtonTypeV3 } from '@indeed/signals-schema/com/indeed/signals/schema/viewjob/RNApplyButtonTypeV3';
import React, {
    type ReactElement,
    type RefObject,
    useCallback,
    useLayoutEffect,
    useRef,
    useState,
} from 'react';
import { Platform, useWindowDimensions } from 'react-native';

export type JobModel = {
    key: string;
    title?: string;
};

export type ViewJobDetailsModel = {
    mobvjtk?: string;
    jobCountry?: string;
};

export interface IndeedApplyButtonProps {
    indeedApplyButtonContainer: IndeedApplyButtonContainer;
    jobModel: JobModel;
    viewJobDetailsModel: ViewJobDetailsModel;
    domain: string;
    variant?: ButtonVariant;
    isSecondaryApplyButton?: boolean;
    buttonText?: string;
    forceEnabled?: boolean;

    iconBefore?: ReactElement;
    iconAfter?: ReactElement;
    onFirstImpression?: () => void;
    onClick?: () => void;
    skipNativeApplyBridge?: boolean;
    hasLoggedImpression: RefObject<boolean>;
    hasLoggedSeen: boolean;
    setHasLoggedSeen: (value: boolean) => void;
    buttonSize?: ButtonSize | undefined;
}

export function IndeedApplyButton({
    indeedApplyButtonContainer,
    jobModel,
    viewJobDetailsModel,
    domain,
    variant,
    isSecondaryApplyButton,
    forceEnabled,
    buttonText,
    buttonSize = 'lg',
    iconBefore,
    iconAfter,
    onFirstImpression,
    onClick,
    skipNativeApplyBridge,
    hasLoggedImpression,
    hasLoggedSeen,
    setHasLoggedSeen,
}: IndeedApplyButtonProps): React.JSX.Element {
    const [hasLoggedVisibility, setHasLoggedVisibility] = useState(false);
    const buttonRef = useRef<BoxRef>(null);
    const i18n = useI18n();
    const logErrorOnce = useLogErrorOnce();
    const { width: screenWidth, height: screenHeight } = useWindowDimensions();

    const { contentHtml, indeedApplyUrl, disabled, tmnUrl } = indeedApplyButtonContainer;
    const { key: jobKey } = jobModel;
    const { mobvjtk: mobtk } = viewJobDetailsModel;

    const { accountKey, ctk, employerOutreachEngagement } = useViewjobOneGraphModel() ?? {};
    const invite = employerOutreachEngagement?.engagements?.[0];
    const rnViewJobDisplay =
        useViewJobDataContext().initialData?.rnViewJobDisplay ?? 'MOBILE_STANDALONE';
    const isDesktopEmbedded = rnViewJobDisplay === 'DESKTOP_EMBEDDED';
    const tareData = useTareData();
    const buildEnvironment = tareData?.appModel?.buildEnvironment;
    const environmentConfig = getEnvironmentConfig(buildEnvironment);
    const rezcowBaseUrl = environmentConfig.rezcowBaseUrl;
    const resumeBaseUrl = environmentConfig.resumeBaseUrl;

    const { isFromEmployerOutreach, outreachSource } = useEmployerOutreachSource();
    const { outreachInfo } = useEmployerOutreachInfo(jobKey) ?? {};

    const handleStartIndeedApply = useHandleStartIndeedApply();
    const { markApplyStart } = useDwellTiming();

    const onClickHandler = useCallback(async () => {
        if (skipNativeApplyBridge) {
            onClick?.();
            return;
        }

        if (tmnUrl) {
            void logTapButton('rn-view-job-tmn-apply', 'tmn-apply-button');
        }

        // Mark apply start for dwell timing
        markApplyStart();
        void updateApplyStartCookie(jobKey, 'i', domain);

        // Log employer outreach apply if this is an employer outreach job
        if (isFromEmployerOutreach && invite && rezcowBaseUrl && resumeBaseUrl) {
            await handleEmployerOutreachApply({
                invite,
                rezcowBaseUrl,
                resumeBaseUrl,
                jobKey,
                outreachSource,
                outreachType: outreachInfo?.outreachType,
                accountKey,
                ctk,
                mobtk,
            });
        }

        // For web, this will just be an anchor tag; we don't have to manually navigate
        if (Platform.OS !== 'web') {
            const result = await handleStartIndeedApply(indeedApplyUrl);
            eventEmitter.emit('finishApply', {
                jobKeys: jobKey ? [jobKey] : [],
                applyType: ApplyType.INDEED_APPLY,
                status: result === NativeApplyResult.SUCCESS
                    ? ApplyStatus.CONFIRMED
                    : result === NativeApplyResult.CANCEL
                        ? ApplyStatus.CANCELLED
                        : ApplyStatus.UNKNOWN,
            });
        }
        //TODO: provide fallback option if the command not exist or get error in the handleStartIndeedApply command
        onClick?.();
    }, [
        skipNativeApplyBridge,
        tmnUrl,
        jobKey,
        domain,
        indeedApplyUrl,
        onClick,
        markApplyStart,
        isFromEmployerOutreach,
        outreachSource,
        outreachInfo,
        accountKey,
        ctk,
        mobtk,
        invite,
        rezcowBaseUrl,
        resumeBaseUrl,
        handleStartIndeedApply,
    ]);

    // Added ref to prevent adding a function as a dependency on useLayoutEffect.
    const onFirstImpressionRef = useRef(onFirstImpression);

    useLayoutEffect(() => {
        if (buttonRef.current && jobKey && !isSecondaryApplyButton) {
            buttonRef.current.measure(
                (
                    _: number,
                    __: number,
                    width: number,
                    height: number,
                    pageX: number,
                    pageY: number,
                ) => {
                    if (!hasLoggedVisibility) {
                        const windowDimensions: ButtonDimensions = {
                            srcWidth: screenWidth,
                            srcHeight: screenHeight,
                            iaButtonX: pageX,
                            iaButtonY: pageY,
                            iaButtonWidth: width,
                            iaButtonHeight: height,
                        };
                        logApplyButtonShowEvent(
                            jobKey,
                            RNApplyButtonTypeV3.indeedApply,
                            mobtk,
                            windowDimensions,
                        );
                        setHasLoggedVisibility(true);
                    }
                },
            );
        }

        if (!hasLoggedImpression.current) {
            hasLoggedImpression.current = true;
            onFirstImpressionRef?.current?.();
        }
    }, [
        jobKey,
        mobtk,
        screenHeight,
        screenWidth,
        isSecondaryApplyButton,
        hasLoggedImpression,
        hasLoggedVisibility,
    ]);

    const buttonDisabled = disabled && !forceEnabled;
    const buttonContent = buttonDisabled ? i18n.gettext('Applied') : buttonText || contentHtml;
    useTrackVisibility(buttonRef, {
        trackingConfig: {
            provider: 'ViewJob',
            trackingKey: mobtk ?? '',
            componentName: 'IndeedApplyButton',
        },
        onBecomeVisible: () => {
            if (!hasLoggedSeen) {
                setHasLoggedSeen(true);
            }
        },
        enabled: !hasLoggedSeen,
    });

    // Guard against empty/malformed indeedApplyUrls
    let isSmartApply = false;
    if (indeedApplyUrl) {
        try {
            const hostname = new URL(indeedApplyUrl).hostname;
            isSmartApply = hostname.endsWith('smartapply.indeed.com');
        } catch (error) {
            isSmartApply = false;
            logErrorOnce('IndeedApplyButton: failed to parse indeedApplyUrl', {
                jobKey,
                indeedApplyUrl,
                hasTmnUrl: !!tmnUrl,
                error: error instanceof Error ? error.message : String(error),
            });
        }
    }

    return (
        <Button
            onPress={() => {
                void onClickHandler();
            }}
            variant={variant}
            ref={buttonRef}
            disabled={buttonDisabled}
            iconBefore={iconBefore}
            iconAfter={iconAfter}
            size={buttonSize}
            href={indeedApplyUrl}
            hrefAttrs={isSmartApply && !isDesktopEmbedded ? undefined : { rel: 'nofollow', target: 'blank' }}
        >
            {buttonContent}
        </Button>
    );
}
